<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.activities', 2),
                        href: meta.links.activities_index,
                    },
                    {
                        name: $tc('messages.duels', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-12">
                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.group')" for="group_id" />
                        <FormSelect
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="">
                        <FormLabel :value="$tc('messages.students', 1)" for="student_id" />
                        <FormSelect
                            :null-option-text="
                                form.group_id
                                    ? $t('messages.select')
                                    : $t('messages.select_X', { X: $tc('messages.groups', 1) })
                            "
                            id="student_id"
                            v-model="form.student_id"
                            :options="selects.students"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.student_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <div class="mt-1">
                        <FormLabel :value="$tc('messages.subject', 1)" for="subject_id" />
                        <FormSelect
                            id="subject_id"
                            v-model="form.subject_id"
                            :options="options.subjects"
                            class="mt-1 w-full"
                            optgroups
                        />
                        <FormError :value="form.errors.subject_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1 text-right">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div>
            <div class="flex flex-col mt-5">
                <div>{{ $t('messages.finished_duels') }}</div>

                <div>
                    <AppWell>
                        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            <AppCard v-for="duel in duels.data" :key="duel.id">
                                <div class="px-2 py-4">
                                    <div class="flex justify-center">
                                        <span>{{ $tc('messages.duels', 1) }}</span>
                                    </div>
                                    <div class="flex justify-center text-gray-500 text-sm">
                                        <span>{{ duel.date_display }}</span>
                                    </div>
                                    <div>
                                        <div
                                            class="flex justify-center items-center gap-4"
                                            :class="{ 'flex-row-reverse': !duel.student_is_challenger }"
                                        >
                                            <div class="flex flex-col items-center">
                                                <img
                                                    :src="duel.challenger_avatar"
                                                    :alt="duel.challenger_name"
                                                    :title="duel.challenger_name"
                                                    class="rounded-full h-20"
                                                />
                                                <div>{{ duel.challenger_name }}</div>
                                                <div>
                                                    <BadgePill :color="duel.challenger_status.color">
                                                        {{ duel.challenger_status.display }}
                                                    </BadgePill>
                                                </div>
                                                <div>{{ duel.challenger_result }}</div>
                                            </div>
                                            <div><IconVs class="h-20 w-20 text-gray-300" /></div>
                                            <div class="flex flex-col items-center">
                                                <img
                                                    :src="duel.challenged_avatar"
                                                    :alt="duel.challenged_name"
                                                    :title="duel.challenged_name"
                                                    class="rounded-full h-20"
                                                />
                                                <div>{{ duel.challenged_name }}</div>
                                                <div>
                                                    <BadgePill :color="duel.challenged_status.color">
                                                        {{ duel.challenged_status.display }}
                                                    </BadgePill>
                                                </div>
                                                <div>{{ duel.challenged_result }}</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="duel.tie_display_message">
                                        <Tippy>
                                            <button>
                                                <IconInfoCircle class="w-6 h-6 text-gray-400" />
                                            </button>
                                            <template #content>
                                                {{ duel.tie_display_message }}
                                            </template>
                                        </Tippy>
                                    </div>
                                    <div class="mt-3">
                                        <InertiaSquareLinkButton
                                            action="show"
                                            :href="duel.links.show"
                                            v-if="duel.links.show"
                                        />
                                    </div>
                                </div>
                            </AppCard>
                        </div>
                    </AppWell>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="duels" />
        </template>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import BadgePill from '@/components/BadgePill.vue';
import axios from 'axios';
import { IconHourglassEmpty, IconInfoCircle, IconVs } from '@tabler/icons-vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import { Tippy } from 'vue-tippy';

export default {
    components: {
        IconInfoCircle,
        Tippy,
        AlertMessage,
        IconHourglassEmpty,
        AppCard,
        AppWell,
        IconVs,
        BadgePill,
        FormSelect,
        FormError,
        SubmitFormButton,
        FormLabel,
        InertiaSquareLinkButton,
        InertiaPagination,
        PanelPage,
    },
    layout: Layout,
    name: 'Index',
    props: ['duels', 'options', 'filters', 'meta'],
    data() {
        return {
            form: this.$inertia.form({
                subject_id: this.filters.subject_id,
                group_id: this.filters.group_id,
                student_id: this.filters.student_id,
                mode: this.filters.mode,
            }),
            selects: {
                students: this.options.students,
            },
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
        fetchStudents() {
            axios.get(this.route('select.group_students.show', { group: this.form.group_id })).then((response) => {
                this.form.student_id = null;
                this.selects.students = response.data.items;
            });
        },
    },
    watch: {
        'form.group_id': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    if (newVal) {
                        this.fetchStudents();
                    } else {
                        this.form.student_id = null;
                        this.selects.students = [];
                    }
                }
            },
            deep: true,
        },
    },
};
</script>
